import React, { useState, useEffect, useRef } from 'react';
import { useSEO } from '../../hooks/useSEO';
import type { Service, Settings } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import OptimizedImage from '../../components/OptimizedImage';

const OurServicesPage: React.FC<{ settings: Settings }> = ({ settings }) => {
    const { data: services, isLoading } = useCollection<Service>('services', { field: 'order', direction: 'asc' });
    const [activeService, setActiveService] = useState('');
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

    useSEO({
        title: `Our Services | ${settings?.companyName || 'ARCH.'}`,
        description: `Explore the range of professional architectural and design services offered by ${settings?.companyName || 'ARCH.'}.`,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);

    useEffect(() => {
        if (services && services.length > 0) {
            setActiveService(services[0].slug);
        }
    }, [services]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveService(entry.target.id);
                }
            });
        }, { rootMargin: '-20% 0px -80% 0px' });

        // FIX: Iterate over object keys for better type safety to resolve 'unknown' type error.
        const currentRefs = sectionRefs.current;
        Object.keys(currentRefs).forEach(key => {
            const ref = currentRefs[key];
            if (ref) observer.observe(ref);
        });

        return () => {
            Object.keys(currentRefs).forEach(key => {
                const ref = currentRefs[key];
                if (ref) observer.unobserve(ref);
            });
        };
    }, [services]);

    const handleNavClick = (slug: string) => {
        sectionRefs.current[slug]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="bg-brand-light text-brand-dark pt-32 pb-16">
            <div className="container mx-auto px-6 max-w-6xl">
                 <div className="text-center max-w-3xl mx-auto mb-16">
                    <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">Our Services</h1>
                    <p className="mt-4 text-lg text-gray-600 font-body">
                        We offer a comprehensive range of design services, from initial concept to final execution, tailored to meet the unique needs of each project.
                    </p>
                 </div>

                 {isLoading && <p className="text-center">Loading services...</p>}

                 {!isLoading && services && services.length > 0 && (
                    <div className="flex flex-col md:flex-row gap-12">
                         {/* Responsive Sticky Sidebar / Tab Bar */}
                        <aside className="w-full md:w-1/4 self-start md:sticky md:top-32 z-10 bg-brand-light/95 backdrop-blur-sm py-2 md:py-0 border-b md:border-b-0 border-gray-200 md:border-none">
                             <nav className="flex flex-row md:flex-col overflow-x-auto md:overflow-x-visible space-x-2 md:space-x-0 md:space-y-2 pb-3 md:pb-0 scrollbar-none">
                                {services.map(service => (
                                    <button 
                                        key={service.slug}
                                        onClick={() => handleNavClick(service.slug)}
                                        className={`px-4 py-2.5 md:p-3 text-left font-semibold text-sm transition-all duration-200 whitespace-nowrap md:w-full rounded-md flex-shrink-0 ${activeService === service.slug ? 'bg-brand-dark text-white' : 'text-gray-500 hover:bg-gray-200 bg-gray-100/50 md:bg-transparent'}`}
                                    >
                                        {service.title}
                                    </button>
                                ))}
                             </nav>
                        </aside>

                        {/* Main Content */}
                        <main className="md:w-3/4 space-y-16">
                            {services.map(service => (
                                <section 
                                    key={service.slug} 
                                    id={service.slug}
                                    ref={(el): void => { sectionRefs.current[service.slug] = el; }}
                                    className="scroll-mt-24"
                                >
                                    <OptimizedImage 
                                        src={service.imageUrl}
                                        size={1200}
                                        alt={service.title}
                                        className="w-full h-64 object-cover mb-8"
                                    />
                                    <h2 className="text-4xl font-bold font-heading mb-4">{service.title}</h2>
                                    <p className="text-gray-600 mb-8 font-body">{service.description}</p>
                                    <div className="space-y-6">
                                        {service.sections.map((section, index) => (
                                            <div key={index}>
                                                <h3 className="text-xl font-semibold font-heading mb-2">{section.title}</h3>
                                                <div 
                                                    className="prose max-w-none text-gray-700"
                                                    dangerouslySetInnerHTML={{ __html: section.content }}
                                                ></div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </main>
                    </div>
                 )}

                 {!isLoading && (!services || services.length === 0) && (
                     <p className="text-center text-gray-500 py-12">No services have been added yet.</p>
                 )}
            </div>
        </div>
    );
};

export default OurServicesPage;